import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { connectState } from '@examdojo/angular/util';
import { TranslocoModule } from '@jsverse/transloco';
import { map } from 'rxjs';
import { IconComponent } from '@examdojo/core/icon';
import { ExamTimeService } from './exam-time.service';

@Component({
  selector: 'dojo-exam-time',
  standalone: true,
  imports: [TranslocoModule, IconComponent],
  template: `
    @if (state.timer; as timer) {
      <div class="flex flex-col gap-2 px-2 py-3">
        <div class="text-secondary flex items-center justify-between">
          <dojo-icon [icon]="'stopwatch'" [size]="16"></dojo-icon>
          <div class="text-sm font-semibold">
            @if (state.isTimeUp) {
              {{ 'exams.time_is_up' | transloco }}
            } @else {
              {{ timer.formattedTime }}
            }
          </div>
        </div>

        <div class="h-2 w-full overflow-hidden rounded-full bg-neutral-100">
          <div
            class="progress-bar h-2 min-w-2 rounded-full transition-all duration-1000 ease-linear"
            [style.width.%]="timer.progress"
            [class.timer-default]="!state.isTimeUp && timer.progress > 20"
            [class.timer-ending]="!state.isTimeUp && timer.progress <= 20"
            [class.bg-neutral-100]="state.isTimeUp"
          ></div>
        </div>
      </div>
    }
  `,
  styleUrl: './exam-time.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExamTimeComponent {
  private readonly examTimeService = inject(ExamTimeService);

  readonly state = connectState({
    timer: this.examTimeService.timerData$.pipe(
      map((state) => {
        if (!state) {
          return null;
        }

        const { remainingSeconds, timeLimitInMinutes } = state;
        const totalSeconds = timeLimitInMinutes * 60;
        const progress = Math.min(100, Math.max(0, (remainingSeconds / totalSeconds) * 100));

        const minutes = Math.floor(remainingSeconds / 60);
        const seconds = remainingSeconds % 60;
        const formattedTime = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

        return { formattedTime, progress };
      }),
    ),
    isTimeUp: this.examTimeService.isTimeUp$,
  });
}
